import { achseVon } from './achsen';
import type { Abfrageergebnis, Darstellung, Katalogkennzahl } from './auswertung';

/**
 * DIE DIAGRAMMARTEN EINES BERICHTS (ÄA-54, Auftrag CRM-89) — was Maske,
 * PDF und Server gleich sagen muessen.
 *
 * OHNE EINFUHR zur Laufzeit ausser `achsen.ts`, wie `kennzahloptionen.ts`:
 * Die Oberflaeche fuehrt diese Datei ueber ihren Quellpfad ein
 * (`@crm/kontrakte/src/diagrammarten`), die API ueber das Paket. Der Grund
 * einer Sperre steht so an der gesperrten Wahl im Baukasten und im PDF mit
 * demselben Wortlaut.
 *
 * ZWEI ARTEN VON GRUENDEN. `untauglich` sagt, warum eine Darstellung zum
 * Ergebnis nicht passt — dann ist die Wahl gesperrt (wie Kreis und
 * gestapelt, Auftrag #33). `fehlendeOptionen` nennt, was die Darstellung an
 * Kennzahloptionen braucht, weil die Tabelle als zweite Form derselben
 * Zahlen (NFA-14) sie zeigen muss: Der Boxplot braucht Min/Max, Median und
 * Quartile, 100 % die Anteile an der Zeile, die kumulierte Linie die laufende
 * Summe. Der Baukasten schaltet sie mit der Wahl ein; fehlen sie in einer
 * gespeicherten Beschreibung, zeichnet jede Ausgabe Balken.
 */

/** Die Optionen einer Kennzahl, die eine Darstellung braucht (ÄA-53). */
export type Darstellungsoption = 'minMax' | 'median' | 'quartile' | 'anteil' | 'kumuliert';

/** Die neuen Darstellungen dieses Auftrags. */
export type Diagrammart = Extract<Darstellung, 'boxplot' | 'histogramm' | 'hundert' | 'kumuliert' | 'heatmap' | 'liegend'>;

export const DIAGRAMMARTEN: readonly Diagrammart[] = ['boxplot', 'histogramm', 'hundert', 'kumuliert', 'heatmap', 'liegend'];

export function istDiagrammart(d: Darstellung): d is Diagrammart {
  return (DIAGRAMMARTEN as readonly string[]).includes(d);
}

/** Die Namen aller Darstellungen, wie Baukasten, Exportkopf und PDF sie nennen. */
export const DARSTELLUNGSNAMEN: Readonly<Record<Darstellung, string>> = {
  balken: 'Balken',
  gestapelt: 'Gestapelte Balken',
  linie: 'Linie',
  tabelle: 'Tabelle',
  kreis: 'Kreis',
  boxplot: 'Boxplot',
  histogramm: 'Histogramm',
  hundert: '100 % gestapelt',
  kumuliert: 'Kumulierte Linie',
  heatmap: 'Heatmap',
  liegend: 'Liegende Balken',
};

/** Wann es die Darstellung gibt, als Satz — neben dem Grund an der gesperrten Wahl (Auftrag #33). */
export const DIAGRAMMART_BEDINGUNG: Readonly<Record<Diagrammart, string>> = {
  boxplot: 'Den Boxplot gibt es für genau eine Kennzahl mit Einzelwerten (Summe oder Durchschnitt je Beleg), ohne Aufteilung.',
  histogramm: 'Das Histogramm gibt es für genau eine Kennzahl mit Einzelwerten (Summe oder Durchschnitt je Beleg), ohne Aufteilung.',
  hundert: '100 % gestapelt gibt es mit einer Aufteilung für genau eine Kennzahl, die sich addiert (Summe oder Anzahl), ohne Werte unter null.',
  kumuliert: 'Die kumulierte Linie gibt es, wenn nach der Zeit gruppiert und nicht aufgeteilt ist und die erste Kennzahl sich addiert.',
  heatmap: 'Die Heatmap gibt es mit einer Aufteilung für genau eine Kennzahl.',
  liegend: 'Liegende Balken gibt es, wenn nach einem Merkmal statt nach der Zeit gruppiert und nicht aufgeteilt ist und alle Kennzahlen eine Einheit haben.',
};

/** Die Spanne (AUS-34), als Satz neben dem Grund. */
export const SPANNE_BEDINGUNG =
  'Die Spanne steht im Balken- und im Liniendiagramm ohne Aufteilung, wenn eine Kennzahl Min/Max oder Quartile eingeschaltet hat.';

function eine(e: Abfrageergebnis, art: string): string | null {
  if (e.kennzahlen.length === 0) return 'Es gibt keine Kennzahl.';
  return e.kennzahlen.length > 1 ? `${art} zeigt eine Kennzahl; gewählt sind ${String(e.kennzahlen.length)}.` : null;
}

/** Hat die Kennzahl eine Einzelwert-Ebene (AUS-16, Auftrag CRM-86)? Sonst der Grund des Katalogs. */
function ohneEinzelwert(k: Katalogkennzahl): string | null {
  if (k.minMax?.moeglich === true) return null;
  return `„${k.name}": ${k.minMax?.grund ?? 'Die Kennzahl hat keine Einzelwerte.'}`;
}

/** Steht die Option fuer die Kennzahl in der Beschreibung? (Wie `optionAn` im Baukasten, ohne die Zeitachse.) */
export function optionGewaehlt(e: Abfrageergebnis, k: Katalogkennzahl, o: Darstellungsoption): boolean {
  return (e.beschreibung[o] ?? []).includes(k.kennung);
}

/**
 * WARUM DIE DARSTELLUNG NICHT ZUM ERGEBNIS PASST — oder null.
 *
 *   boxplot     AUS-35  eine Kennzahl mit Einzelwerten, keine Aufteilung
 *   histogramm  AUS-36  eine Kennzahl mit Einzelwerten, keine Aufteilung
 *   hundert     AUS-37  Aufteilung, eine additive Kennzahl, nichts unter null
 *   kumuliert   AUS-38  Zeitachse, keine Aufteilung, erste Kennzahl additiv —
 *                       Kumulieren von Quoten und Durchschnitten ist gesperrt
 *   heatmap     AUS-39  Aufteilung, eine Kennzahl
 *   liegend     AUS-40  Merkmal statt Zeit, keine Aufteilung, eine Einheit
 */
export function diagrammartUntauglich(d: Diagrammart, e: Abfrageergebnis): string | null {
  const k = e.kennzahlen[0];
  const t = e.aufteilung ?? null;
  switch (d) {
    case 'boxplot':
    case 'histogramm': {
      const name = d === 'boxplot' ? 'Ein Boxplot' : 'Ein Histogramm';
      const zahl = eine(e, name);
      if (zahl !== null || k === undefined) return zahl;
      const einzel = ohneEinzelwert(k);
      if (einzel !== null) return einzel;
      if (t !== null) return `${name} verteilt die Einzelwerte je Gruppe; eine Aufteilung hat es nicht.`;
      return null;
    }
    case 'hundert': {
      if (t === null) return '100 % zeigt die Anteile der Werte einer Aufteilung — wählen Sie links „Aufteilen nach".';
      const zahl = eine(e, '100 % gestapelt');
      if (zahl !== null || k === undefined) return zahl;
      if (k.optionen?.anteil.moeglich !== true) {
        return `„${k.name}": ${k.optionen?.anteil.moeglich === false ? k.optionen.anteil.grund : 'Die Werte addieren sich nicht zu einem Ganzen.'}`;
      }
      const negativ = e.zeilen.some((z) => (z.teile ?? []).some((zelle) => Number(zelle[0]?.wert ?? 0) < 0));
      return negativ ? 'Werte unter null haben keinen Anteil an der Zeile.' : null;
    }
    case 'kumuliert': {
      if (k === undefined) return 'Es gibt keine Kennzahl.';
      if (e.gruppierung?.zeitlich !== true) return 'Kumuliert wird entlang einer Zeitachse — gruppieren Sie nach Tag, Woche, Monat, Quartal oder Jahr.';
      if (t !== null) return 'Die kumulierte Linie zeigt den Zeitraum ohne Aufteilung.';
      if (k.optionen?.kumuliert.moeglich !== true) {
        return `„${k.name}": ${k.optionen?.kumuliert.moeglich === false ? k.optionen.kumuliert.grund : 'Die Werte addieren sich nicht.'}`;
      }
      return null;
    }
    case 'heatmap': {
      if (t === null) return 'Eine Heatmap braucht zwei Dimensionen — wählen Sie links „Aufteilen nach".';
      return eine(e, 'Eine Heatmap');
    }
    case 'liegend': {
      if (k === undefined) return 'Es gibt keine Kennzahl.';
      if (e.gruppierung === null) return 'Liegende Balken brauchen eine Gruppierung.';
      if (e.gruppierung.zeitlich) return 'Ein Verlauf über die Zeit gehört auf die waagrechte Achse — dafür sind Balken und Linie da.';
      if (t !== null) return 'Liegende Balken zeigen die Gruppen ohne Aufteilung; dafür gibt es gestapelte Balken und die Heatmap.';
      if (e.kennzahlen.some((_, i) => achseVon(e.kennzahlen, i) === 'rechts')) {
        return 'Liegende Balken haben eine Werteachse; die Kennzahlen tragen zwei Einheiten.';
      }
      return null;
    }
  }
}

/**
 * WAS DIE DARSTELLUNG AN OPTIONEN BRAUCHT UND NICHT HAT. Die Optionen der
 * ersten Kennzahl; bei 100 % dazu der Anteil an der Zeile (`anteilAn`).
 */
export function fehlendeOptionen(d: Darstellung, e: Abfrageergebnis): readonly Darstellungsoption[] {
  const k = e.kennzahlen[0];
  if (k === undefined) return [];
  const brauche: readonly Darstellungsoption[] =
    d === 'boxplot' ? ['minMax', 'median', 'quartile'] : d === 'hundert' ? ['anteil'] : d === 'kumuliert' ? ['kumuliert'] : [];
  const fehlt = brauche.filter((o) => !optionGewaehlt(e, k, o));
  return d === 'hundert' && fehlt.length === 0 && e.beschreibung.anteilAn !== 'zeile' ? ['anteil'] : fehlt;
}

/** Was eine neue Darstellung wirklich zeigt: sie selbst, oder Balken, wenn sie nicht passt oder Optionen fehlen. */
export function diagrammartWirksam(d: Diagrammart, e: Abfrageergebnis): Diagrammart | 'balken' {
  if (diagrammartUntauglich(d, e) !== null || fehlendeOptionen(d, e).length > 0) return 'balken';
  if (d === 'histogramm' && e.histogramm == null) return 'balken';
  return d;
}

/**
 * AUS-40: LIEGENDE BALKEN VON SELBST. Bei mehr als zwoelf Gruppen oder
 * Namen, die die stehende Achse kuerzen muesste (laenger als 14 Zeichen,
 * Diagramm.tsx), liest man liegende Balken besser — wenn sie taugen.
 */
export const LIEGEND_AB_GRUPPEN = 12;
export const LIEGEND_AB_ZEICHEN = 14;

export function liegendEmpfohlen(e: Abfrageergebnis): boolean {
  if (diagrammartUntauglich('liegend', e) !== null) return false;
  return e.zeilen.length > LIEGEND_AB_GRUPPEN || e.zeilen.some((z) => z.bezeichnung.length > LIEGEND_AB_ZEICHEN);
}

/**
 * AUS-34 (ÄA-56, Auftrag CRM-92): WAS DAS BILD AN KENNZAHLOPTIONEN ZEICHNET.
 * `art` ist die Darstellung, die das Bild wirklich zeigt.
 *
 *   lage       Min/Max als Linie mit Enden, Q1–Q3 als Kasten, der Median als
 *              Querstrich — an Balken (auch liegend); an der Linie als Baender
 *              und gestrichelte Linie
 *   anteil     der Anteil als Beschriftung am Balken, am Stueck, am Segment
 *   kumuliert  die laufende Summe „zusaetzlich" als eigene Linie
 *   n          die Zahl der Einzelwerte in der Kurzinfo
 *
 * Min, Max, Median, Quartile und n gelten der Zeile. Mit Aufteilung zeigt
 * das Bild Teile der Zeile — dort, im Kreis und in den eigenen Diagrammarten
 * sind sie nicht eindeutig zu zeichnen; der Hinweis sagt, dass sie in der
 * Tabelle stehen. Was eine Darstellung schon von selbst zeigt (der Boxplot
 * die Lage, 100 % den Anteil, die kumulierte Linie die laufende Summe),
 * nennt der Hinweis nicht.
 */
export type Bildoptionen = {
  readonly lage: boolean;
  readonly anteil: boolean;
  readonly kumuliert: boolean;
  readonly n: boolean;
  /** Was gewaehlt ist, aber nicht im Bild steht — oder null. */
  readonly hinweis: string | null;
};

const KEINE_BILDOPTIONEN: Bildoptionen = { lage: false, anteil: false, kumuliert: false, n: false, hinweis: null };

export function bildoptionen(e: Abfrageergebnis, art: Darstellung, gezeigt: boolean): Bildoptionen {
  if (!gezeigt || art === 'tabelle') return KEINE_BILDOPTIONEN;
  const aufgeteilt = e.aufteilung != null;
  const gewaehlt = (o: Darstellungsoption | 'n'): boolean =>
    e.kennzahlen.some((k) => (e.beschreibung[o] ?? []).includes(k.kennung));
  /* AUS-33: laufende Summen gibt es nur auf einer Zeitachse; „statt" steht schon im Wert. */
  const kumuliertGewaehlt =
    gewaehlt('kumuliert') && e.gruppierung?.zeitlich === true && e.beschreibung.kumuliertAls !== 'statt';
  const gewohnt = art === 'balken' || art === 'linie' || art === 'gestapelt' || art === 'liegend';
  const kann = {
    lage: (gewohnt && !aufgeteilt) || art === 'boxplot',
    anteil: gewohnt || art === 'kreis' || art === 'hundert',
    kumuliert: ((art === 'balken' || art === 'linie') && !aufgeteilt) || art === 'kumuliert',
    n: gewohnt && !aufgeteilt,
  };
  const fehlt = [
    ...(!kann.lage && gewaehlt('minMax') ? ['Min und Max'] : []),
    ...(!kann.lage && gewaehlt('median') ? [gewaehlt('quartile') ? 'Median und Quartile' : 'Median'] : []),
    ...(!kann.anteil && gewaehlt('anteil') ? ['Anteil'] : []),
    ...(!kann.kumuliert && kumuliertGewaehlt ? ['die laufende Summe'] : []),
    ...(!kann.n && gewaehlt('n') ? ['n'] : []),
  ];
  const grund = aufgeteilt
    ? 'mit Aufteilung zeigt das Bild Teile der Zeile, die Werte gelten aber der ganzen Zeile.'
    : art === 'kreis'
      ? 'der Kreis zeigt nur Anteile.'
      : 'diese Darstellung zeichnet das nicht.';
  const liste = fehlt.length === 1 ? fehlt[0]! : `${fehlt.slice(0, -1).join(', ')} und ${fehlt[fehlt.length - 1]!}`;
  return {
    lage: kann.lage && art !== 'boxplot' && (gewaehlt('minMax') || gewaehlt('median')),
    anteil: kann.anteil && art !== 'hundert' && gewaehlt('anteil'),
    kumuliert: kann.kumuliert && art !== 'kumuliert' && kumuliertGewaehlt,
    n: kann.n && gewaehlt('n'),
    hinweis: fehlt.length === 0 ? null : `${liste} ${fehlt.length === 1 && !liste.includes(' und ') ? 'steht' : 'stehen'} in der Tabelle — ${grund}`,
  };
}

/**
 * AUS-41: DIE DARSTELLUNG IN WORTEN fuer Exportkopf und PDF — die gewaehlte
 * und, wenn sie nicht passt, was statt ihrer gezeigt wird und warum.
 */
export function darstellungstext(e: Abfrageergebnis): string {
  const d = e.beschreibung.darstellung;
  if (!istDiagrammart(d) || diagrammartWirksam(d, e) === d) return DARSTELLUNGSNAMEN[d];
  const grund =
    diagrammartUntauglich(d, e) ??
    (fehlendeOptionen(d, e).length > 0 ? 'Es fehlen Kennzahloptionen, die sie braucht.' : 'Die Klassen fehlen.');
  return `${DARSTELLUNGSNAMEN[d]} gewählt, gezeigt werden Balken — ${grund}`;
}

/* ── Histogramm (AUS-36) ─────────────────────────────────────────────────── */

/** Eine runde Schrittweite: 1, 2, 2,5 oder 5 mal einer Zehnerpotenz, mindestens ein Hundertstel. */
function runderSchritt(roh: number): number {
  if (!(roh > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(roh));
  const f = roh / p;
  const schritt = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
  return Math.max(0.01, schritt);
}

/** Ohne Gleitkommareste: 0.1 + 0.2 ist hier 0.3. */
function glatt(x: number): number {
  return Number(x.toFixed(2));
}

/**
 * AUS-36: RUNDE KLASSENGRENZEN AUS MIN UND MAX. Etwa `ziel` gleich breite
 * Klassen mit runder Breite; die erste Grenze liegt auf oder unter dem
 * kleinsten Wert, die letzte ueber dem groessten. Jeder Einzelwert liegt so
 * in genau einer Klasse [Grenze, naechste Grenze).
 */
export function automatischeGrenzen(min: number, max: number, ziel = 6): readonly number[] {
  const schritt = runderSchritt((max - min) / ziel);
  const start = glatt(Math.floor(min / schritt) * schritt);
  const grenzen = [start];
  for (let i = 1; grenzen[grenzen.length - 1]! <= max && i <= 1000; i += 1) grenzen.push(glatt(start + i * schritt));
  return grenzen;
}

const ZAHL = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

/** Eine Grenze in Worten, mit Einheitszeichen, wo die Kennzahl eins hat: „250 €", „8 Tage". */
function grenzwort(g: string, einheit: Katalogkennzahl['einheit']): string {
  const z = ZAHL.format(Number(g));
  if (einheit === 'EUR') return `${z} €`;
  if (einheit === 'Prozent') return `${z} %`;
  if (einheit === 'Tage') return `${z} Tage`;
  if (einheit === 'Stunden') return `${z} Std.`;
  return z;
}

/**
 * AUS-36: DIE KLASSEN ZU GRENZEN. Automatische Grenzen schliessen alle Werte
 * ein — die Klassen liegen dazwischen. Eigene Grenzen sind Schnitte: Davor
 * und dahinter steht je eine offene Klasse („unter 8", „ab 31").
 */
export function klassenAus(
  grenzen: readonly string[],
  offen: boolean,
  einheit: Katalogkennzahl['einheit'],
): readonly { readonly von: string | null; readonly bis: string | null; readonly text: string }[] {
  const innen = grenzen.slice(1).map((bis, i) => {
    const von = grenzen[i]!;
    return { von, bis, text: `${grenzwort(von, einheit)} bis unter ${grenzwort(bis, einheit)}` };
  });
  if (!offen) return innen;
  const erste = grenzen[0]!;
  const letzte = grenzen[grenzen.length - 1]!;
  return [
    { von: null, bis: erste, text: `unter ${grenzwort(erste, einheit)}` },
    ...innen,
    { von: letzte, bis: null, text: `ab ${grenzwort(letzte, einheit)}` },
  ];
}

/**
 * In welche Klasse ein Wert faellt — dieselbe Regel wie `width_bucket` in
 * PostgreSQL: gezaehlt wird, wie viele Grenzen nicht ueber dem Wert liegen.
 * Bei automatischen Grenzen ist die erste Klasse die zwischen der ersten und
 * der zweiten Grenze. null, wenn der Wert ausserhalb liegt.
 */
export function klasseVon(wert: number, grenzen: readonly number[], offen: boolean): number | null {
  const fach = grenzen.filter((g) => g <= wert).length;
  const i = offen ? fach : fach - 1;
  const anzahl = offen ? grenzen.length + 1 : grenzen.length - 1;
  return i >= 0 && i < anzahl ? i : null;
}

/* ── Boxplot (AUS-35) ────────────────────────────────────────────────────── */

export type Fuenfwerte = { readonly min: number; readonly q1: number; readonly median: number; readonly q3: number; readonly max: number };

/**
 * AUS-35: Die fuenf Werte einer Zeile, oder null, wenn einer fehlt (eine
 * Gruppe ohne Einzelwert). Gezeichnet wird mit Zahlen; die Tabelle zeigt
 * die Festkommawerte des Servers (RND-01).
 */
export function fuenfwerte(w: {
  readonly min?: string | null;
  readonly q1?: string | null;
  readonly median?: string | null;
  readonly q3?: string | null;
  readonly max?: string | null;
} | undefined): Fuenfwerte | null {
  if (w === undefined) return null;
  const roh = [w.min, w.q1, w.median, w.q3, w.max];
  if (roh.some((x) => x == null)) return null;
  const [min, q1, median, q3, max] = roh.map(Number) as [number, number, number, number, number];
  return { min, q1, median, q3, max };
}

/* ── Heatmap (AUS-39) ────────────────────────────────────────────────────── */

export type Heatmapskala = {
  readonly tief: number;
  readonly hoch: number;
  /** 0 beim kleinsten Wert, 1 beim groessten; gleiche Werte ueberall 1. */
  readonly anteil: (w: number) => number;
  /** Die Werte der Legende: die Untergrenze jeder der fuenf Stufen von `stufeVon`. */
  readonly stufen: readonly number[];
};

/**
 * AUS-39: DIE SKALA DER HEATMAP — linear vom kleinsten zum groessten Wert der
 * Zellen; leere Zellen zaehlen nicht. Die Farbe steht in fuenf Stufen
 * (`stufeVon`), damit die Legende jede Stufe benennen kann und zwei Zellen
 * gleicher Stufe gleich aussehen.
 */
export function heatmapskala(werte: readonly (number | null)[]): Heatmapskala {
  const da = werte.filter((w): w is number => w !== null && Number.isFinite(w));
  const tief = da.length === 0 ? 0 : Math.min(...da);
  const hoch = da.length === 0 ? 0 : Math.max(...da);
  const spanne = hoch - tief;
  return {
    tief,
    hoch,
    anteil: (w) => (spanne === 0 ? 1 : Math.min(1, Math.max(0, (w - tief) / spanne))),
    /* Fuenftel wie `stufeVon` — sonst nennt die Legende „ab 325" fuer eine Zelle, die ab 280 die Farbe der Stufe 4 traegt. */
    stufen: [0, 1, 2, 3, 4].map((i) => glatt(tief + (spanne * i) / 5)),
  };
}

/** Die Farbstufe einer Zelle, 1 (hell) bis 5 (kraeftig). */
export function stufeVon(anteil: number): 1 | 2 | 3 | 4 | 5 {
  return Math.min(5, Math.floor(anteil * 5) + 1) as 1 | 2 | 3 | 4 | 5;
}

/* ── Die Zahlen der Bilder — Maske und PDF zeichnen dieselben ────────────── */

const zahlOderNull = (w: string | null | undefined): number | null => (w == null ? null : Number(w));

/**
 * AUS-38: DIE KUMULIERTE LINIE. Die laufende Summe der ersten Kennzahl vom
 * Server (AUS-33; bei „statt" ist sie der Wert selbst), je Vergleich seine
 * laufende Summe (das Vorjahr kumuliert als zweite Linie) und das Ziel,
 * Zeile fuer Zeile aufsummiert — null, wo eine Zeile kein Ziel hat.
 */
export function kumulierteReihen(e: Abfrageergebnis): {
  readonly haupt: readonly (number | null)[];
  readonly vergleiche: readonly { readonly name: string; readonly werte: readonly (number | null)[] }[];
  readonly ziel: readonly (number | null)[] | null;
} {
  const statt = e.beschreibung.kumuliertAls === 'statt';
  const haupt = e.zeilen.map((z) => zahlOderNull(statt ? z.werte[0]?.wert : z.werte[0]?.kumuliert));
  const vergleiche = e.vergleiche.map((v, j) => ({
    name: `${v.name} kumuliert`,
    werte: e.zeilen.map((z) => {
      const w = z.werte[0]?.vergleiche[j];
      return zahlOderNull(statt ? w?.wert : w?.kumuliert);
    }),
  }));
  let lauf = 0;
  let luecke = false;
  const ziel = e.zeilen.map((z) => {
    const s = z.werte[0]?.ziel?.ziel;
    if (s == null) luecke = true;
    lauf += Number(s ?? 0);
    return luecke ? null : glatt(lauf);
  });
  return { haupt, vergleiche, ziel: ziel.every((x) => x === null) ? null : ziel };
}

/**
 * AUS-37: 100 % GESTAPELT. Je Zeile die Anteile an der Zeile (AUS-30,
 * `anteilAn: 'zeile'`) fuer die Werte im Bild, dazu „Übrige" als Rest auf
 * 100 — nie unter null, wo die eine Stelle der Anteile rundet.
 */
export function hundertAnteile(e: Abfrageergebnis): {
  readonly namen: readonly string[];
  readonly zeilen: readonly (readonly (number | null)[])[];
} {
  const t = e.aufteilung;
  if (t == null) return { namen: [], zeilen: [] };
  const mitUebrigen = t.imBild < t.werte.length;
  const namen = [...t.werte.slice(0, t.imBild).map((w) => w.bezeichnung), ...(mitUebrigen ? ['Übrige'] : [])];
  const zeilen = e.zeilen.map((z) => {
    const gezeigt = t.werte.slice(0, t.imBild).map((_, j) => zahlOderNull(z.teile?.[j]?.[0]?.anteil));
    if (!mitUebrigen) return gezeigt;
    const da = gezeigt.some((x) => x !== null);
    const rest = 100 - gezeigt.reduce<number>((a, x) => a + (x ?? 0), 0);
    return [...gezeigt, da ? Math.max(0, glatt(rest)) : null];
  });
  return { namen, zeilen };
}

/**
 * AUS-40: DIE FOLGE DER LIEGENDEN BALKEN — die Zeilen nach dem Wert der
 * ersten Kennzahl, der groesste oben; ohne Wert ans Ende.
 */
export function liegendeFolge(e: Abfrageergebnis): readonly number[] {
  const wert = (i: number): number => Number(e.zeilen[i]?.werte[0]?.wert ?? Number.NEGATIVE_INFINITY);
  return e.zeilen.map((_, i) => i).sort((a, b) => wert(b) - wert(a) || a - b);
}

/** AUS-39: die Zellen der Heatmap — je Zeile und Wert der Aufteilung (alle, nicht nur die im Bild). */
export function heatmapZellen(e: Abfrageergebnis): readonly (readonly (number | null)[])[] {
  const t = e.aufteilung;
  if (t == null) return [];
  return e.zeilen.map((z) => t.werte.map((_, j) => zahlOderNull(z.teile?.[j]?.[0]?.wert)));
}
